<template>
  <div class="vendor-header vendor-header--glass">
    <div class="vendor-header-row">
      <div class="vendor-header-breadcrumb">
        <button class="vendor-header-back-btn" @click="goBack" aria-label="Go back" title="Back">
          <i class="bi bi-arrow-left"></i>
        </button>
        <a href="#">Home</a> &gt; <a href="#">Vendor</a> &gt; Vendor Center
      </div>
      <div v-if="showAnnaAssistantButton" class="vendor-header-anna-actions">
        <SecondaryButton
          label="Anna AI Agent"
          icon="bi bi-stars"
          :custom-class="{ 'vendor-header-anna-btn--active': annaAssistantOpen }"
          title="Open Anna AI Agent"
          @click="toggleAnnaAssistant"
        />
      </div>
    </div>

    <div class="vendor-header-filters vendor-header-filters-row">
      <div class="vendor-header-filters-group">
        <LabelDropdown
          label="Status"
          select-id="statusSelect"
          v-model="localVendorFilter"
          inline
          :options="statusOptions"
        />
        <LabelDropdown
          label="Vendor Type"
          select-id="vendorRoleSelect"
          v-model="localVendorRoleFilter"
          inline
          searchable
          custom-class="vendor-header-filter--role"
          :options="vendorRoleFilterOptions"
        />
        <LabelDropdown
          label="Vendor Scope"
          select-id="vendorScopeSelect"
          v-model="localVendorScope"
          inline
          custom-class="vendor-header-filter--scope"
          :options="vendorScopeOptions"
        />
        <CenterGlobalSearch
          v-if="isAllVendorsScope"
          :consultants="[]"
          :customers="[]"
          :vendors="vendors"
          placeholder="Search vendors..."
          :selected-label="globalSearchLabel"
          @select="$emit('global-search-selected', $event)"
          @clear="$emit('global-search-cleared')"
        />
      </div>

      <div class="vendor-header-actions">
        <PrimaryButton
          label="Add New Vendor"
          @click="goToAddVendor"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { useAnnaAssistant } from '@/composables/useAnnaAssistant';
import CenterGlobalSearch from '@/components/CenterGlobalSearch.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import { VENDOR_ROLE_FILTER_OPTIONS } from '@/constants/vendorRoles.js';

export default {
  name: 'VendorHeader',
  components: { CenterGlobalSearch, LabelDropdown, PrimaryButton, SecondaryButton },
  props: {
    vendorFilter: {
      type: String,
      default: 'Active'
    },
    vendorRoleFilter: {
      type: String,
      default: ''
    },
    vendorScope: {
      type: String,
      default: 'individual',
      validator: (v) => ['all', 'individual'].includes(v)
    },
    vendors: {
      type: Array,
      default: () => []
    },
    globalSearchLabel: {
      type: String,
      default: ''
    },
    showAnnaAssistantButton: {
      type: Boolean,
      default: true
    }
  },
  setup() {
    const { isOpen, toggle } = useAnnaAssistant();
    return {
      annaAssistantOpen: isOpen,
      toggleAnnaAssistant: toggle
    };
  },
  emits: ['filter-changed', 'vendor-role-changed', 'vendor-scope-changed', 'global-search-selected', 'global-search-cleared'],
  data() {
    return {
      localVendorFilter: this.vendorFilter,
      localVendorRoleFilter: this.vendorRoleFilter,
      localVendorScope: this.vendorScope
    };
  },
  computed: {
    isAllVendorsScope() {
      return this.localVendorScope === 'all';
    },
    statusOptions() {
      return [
        { value: 'All', label: 'All' },
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ];
    },
    vendorRoleFilterOptions() {
      return VENDOR_ROLE_FILTER_OPTIONS;
    },
    vendorScopeOptions() {
      return [
        { value: 'all', label: 'All Vendors' },
        { value: 'individual', label: 'Selected Vendor' },
      ];
    },
  },
  methods: {
    goToAddVendor() {
      const companyId = this.$route.params.id;
      this.$router.push({ name: 'AddNewVendor', params: { id: companyId } });
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        const companyId = this.$route.params.id;
        this.$router.push(`/company/${companyId}/dashboard/overview`);
      }
    }
  },
  watch: {
    localVendorFilter(newVal) {
      if (newVal !== this.vendorFilter) {
        this.$emit('filter-changed', newVal);
      }
    },
    vendorFilter(newVal) {
      this.localVendorFilter = newVal;
    },
    vendorRoleFilter(newVal) {
      this.localVendorRoleFilter = newVal;
    },
    localVendorRoleFilter(newVal) {
      if (newVal !== this.vendorRoleFilter) {
        this.$emit('vendor-role-changed', newVal);
      }
    },
    vendorScope(newVal) {
      this.localVendorScope = newVal;
    },
    localVendorScope(newVal) {
      if (newVal !== this.vendorScope) {
        this.$emit('vendor-scope-changed', newVal);
      }
    }
  }
};
</script>

<style src="../styles/VendorHeader.css"></style>
